/**
 * Dropdown to Buttons Converter for Shopify Prestige Theme
 * Converts variant popover dropdowns into styled button groups
 */

const CONFIG = {
  dropdownName: 'Purchase Option',
  labelMappings: {
    'Purchase by Roll': 'Purchase by Roll',
    'Purchase by Sample': 'Order Sample'
  }
};

// ============================================================================
// MAIN FUNCTIONALITY - DO NOT EDIT BELOW
// ============================================================================

(function() {
  'use strict';

  let selectedStates = {};

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  function init() {
    injectCSS();
    processAllFieldsets();
    setTimeout(() => {
      watchForReplacements();
    }, 1000);
  }

  function injectCSS() {
    const style = document.createElement('style');
    style.id = 'dropdown-to-buttons-style';
    style.textContent = `
      /* Hide original dropdown */
      .variant-picker__option.converted-to-buttons > .relative > button.select,
      .variant-picker__option.converted-to-buttons > .relative > x-popover {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        position: absolute !important;
        height: 0 !important;
        width: 0 !important;
        overflow: hidden !important;
      }
      
      /* Force stable height on the fieldset to prevent content jumping */
      .variant-picker__option.converted-to-buttons {
        min-height: 110px !important;
        position: relative !important;
      }
      
      /* Stable wrapper with reserved space */
      .dropdown-buttons-wrapper {
        min-height: 60px !important;
        height: 60px !important;
        margin-top: 8px !important;
        position: relative !important;
        display: block !important;
      }
      
      /* Button container */
      .dropdown-buttons-container {
        display: flex !important;
        gap: 10px;
        flex-wrap: wrap;
        align-items: center;
        position: relative !important;
        z-index: 1 !important;
      }
      
      /* Selected button style */
      .dropdown-button.selected {
        background-color: #143346 !important;
        color: white !important;
        border-color: #143346 !important;
      }
      
      /* Unselected button style */
      .dropdown-button:not(.selected) {
        background-color: white !important;
        color: #333 !important;
        border-color: #ddd !important;
      }
      
      /* Remove all transitions */
      .dropdown-button,
      .dropdown-buttons-container,
      .dropdown-buttons-wrapper,
      .variant-picker__option.converted-to-buttons {
        transition: none !important;
      }
      
      /* Hover state */
      .dropdown-button:not(.selected):hover {
        border-color: #143346 !important;
      }
      
      /* Prevent the variant picker from collapsing during updates */
      variant-picker {
        min-height: 200px !important;
      }
      
      /* Mobile responsive */
      @media (max-width: 768px) {
        .dropdown-buttons-container {
          flex-direction: column !important;
        }
        .dropdown-button {
          width: 100% !important;
        }
        .dropdown-buttons-wrapper {
          min-height: 120px !important;
          height: 120px !important;
        }
        .variant-picker__option.converted-to-buttons {
          min-height: 170px !important;
        }
        variant-picker {
          min-height: 300px !important;
        }
      }
    `;
    document.head.appendChild(style);
  }

  function processAllFieldsets() {
    const variantFieldsets = document.querySelectorAll('.variant-picker__option');
    
    variantFieldsets.forEach((fieldset, index) => {
      const legend = fieldset.querySelector('legend');
      
      if (legend && legend.textContent.trim().toLowerCase().includes(CONFIG.dropdownName.toLowerCase())) {
        const fieldsetId = fieldset.dataset.fieldsetId || `fieldset-${index}`;
        fieldset.dataset.fieldsetId = fieldsetId;
        
        const existingWrapper = fieldset.querySelector('.dropdown-buttons-wrapper');
        
        if (existingWrapper) {
          // Update existing buttons
          updateExistingButtons(fieldset, fieldsetId);
        } else {
          // Create new buttons
          convertPopoverToButtons(fieldset, fieldsetId);
        }
      }
    });
  }

  function updateExistingButtons(fieldset, fieldsetId) {
    const wrapper = fieldset.querySelector('.dropdown-buttons-wrapper');
    if (!wrapper) return;
    
    const buttons = wrapper.querySelectorAll('.dropdown-button');
    const radioInputs = fieldset.querySelectorAll('input[type="radio"]');
    
    const checkedRadio = Array.from(radioInputs).find(radio => radio.checked);
    
    if (checkedRadio) {
      selectedStates[fieldsetId] = checkedRadio.value;
      
      buttons.forEach(button => {
        if (button.dataset.value === checkedRadio.value) {
          button.classList.add('selected');
        } else {
          button.classList.remove('selected');
        }
      });
    }
  }

  function watchForReplacements() {
    const observer = new MutationObserver((mutations) => {
      let needsUpdate = false;
      
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          for (const node of mutation.addedNodes) {
            if (node.nodeType === 1) {
              if (node.classList?.contains('variant-picker__option') || 
                  node.querySelector?.('.variant-picker__option')) {
                needsUpdate = true;
                break;
              }
            }
          }
        }
        if (needsUpdate) break;
      }
      
      if (needsUpdate) {
        // Process immediately without delay
        requestAnimationFrame(() => {
          processAllFieldsets();
        });
      }
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: false
    });
  }

  function convertPopoverToButtons(fieldset, fieldsetId) {
    if (fieldset.querySelector('.dropdown-buttons-wrapper')) {
      return;
    }
    
    fieldset.classList.add('converted-to-buttons');
    
    const radioInputs = fieldset.querySelectorAll('input[type="radio"]');
    if (radioInputs.length === 0) return;

    const options = Array.from(radioInputs).map(radio => {
      const label = radio.closest('label');
      const optionText = label ? label.querySelector('span').textContent.trim() : '';
      return {
        radioInput: radio,
        value: radio.value,
        text: optionText
      };
    });

    if (options.length === 0) return;

    // Create stable wrapper
    const wrapper = document.createElement('div');
    wrapper.className = 'dropdown-buttons-wrapper';

    const buttonContainer = document.createElement('div');
    buttonContainer.className = 'dropdown-buttons-container';

    const checkedRadio = Array.from(radioInputs).find(radio => radio.checked);
    const previouslySelected = selectedStates[fieldsetId];
    const initialSelection = previouslySelected || (checkedRadio ? checkedRadio.value : null);

    const buttons = [];
    options.forEach(option => {
      const button = createButton(option);
      buttons.push(button);
      buttonContainer.appendChild(button);
      
      if (initialSelection && option.value === initialSelection) {
        button.classList.add('selected');
      }
      
      button.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        // Save state before triggering change
        selectedStates[fieldsetId] = option.value;
        
        // Update visuals instantly
        buttons.forEach(btn => btn.classList.remove('selected'));
        button.classList.add('selected');
        
        // Update radio
        option.radioInput.checked = true;
        
        // Trigger change event for Shopify
        const changeEvent = new Event('change', { bubbles: true });
        option.radioInput.dispatchEvent(changeEvent);
      });
    });
    
    wrapper.appendChild(buttonContainer);
    
    // Insert wrapper in the correct position
    const optionInfo = fieldset.querySelector('.variant-picker__option-info');
    if (optionInfo) {
      optionInfo.parentNode.insertBefore(wrapper, optionInfo.nextSibling);
    } else {
      const legend = fieldset.querySelector('legend');
      if (legend && legend.parentNode) {
        legend.parentNode.insertBefore(wrapper, legend.nextSibling);
      } else {
        fieldset.appendChild(wrapper);
      }
    }
  }

  function createButton(option) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'dropdown-button';
    button.dataset.value = option.value;
    
    const buttonLabel = CONFIG.labelMappings[option.text] || option.text;
    button.textContent = buttonLabel;
    
    button.style.cssText = `
      padding: 12px 24px;
      border: 1px solid #ddd;
      cursor: pointer;
      font-family: inherit;
      font-size: inherit;
      border-radius: 4px;
      white-space: nowrap;
      min-height: 48px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    `;
    
    return button;
  }

})();